import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { ResetTwoIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { getCountries } from '../../../../api/countries/get-countries'
import { getOtoIndividualProducts } from '../../../../api/products/get-oto-individual-products'
import { OtoIndividualProductsFilters } from '../../../../lib/types/models/product/product-oto-individual'
import { filterDuplicates } from '../../../../lib/utils/functions/filter-duplicates'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { childAges } from '../../../../views/pcr-personal-space/utils'
import CountryFlag from '../../../elements/country-flag'
import {
  getOtoIndividualProducts as getLoadedProducts,
  OTO_INDIVIDUAL_NUMBER_OF_HOURS,
  ProductJourneyPlaceRow
} from '../utils/oto-individual-utils'

type Props = {
  tableTitle?: string
  journeyPlaces?: Pick<ProductJourneyPlaceRow, 'id'>[]
  isLoading?: boolean
  onSelectJourneyPlaces: (journeyPlaces: ProductJourneyPlaceRow[]) => void
  onSubmit: () => void
}

const ProductJourneyPlaceListSelection = ({
  tableTitle = 'Sélectionner un ou plusieurs lieu(x) de séjour',
  journeyPlaces,
  isLoading,
  onSelectJourneyPlaces,
  onSubmit
}: Props) => {
  const [filter, setFilter] = useState<OtoIndividualProductsFilters>({})

  const {
    data: productRows,
    isPending: isProductsPending,
    isError: isProductsError
  } = useQuery({
    queryKey: ['oto-individual-products', filter],
    queryFn: () => getOtoIndividualProducts(filter)
  })

  // the endpoint returns one row per product per number of weeks; only the products matter here
  const products = useMemo(() => getLoadedProducts(productRows ?? []), [productRows])

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  // one row per journey place. The backend filters the products, not their journey places,
  // so a matching product still carries the places that do not match the filter
  const rows: ProductJourneyPlaceRow[] = useMemo(
    () =>
      products.flatMap((product) =>
        product.journeyPlaces
          .filter(
            (journeyPlace) =>
              (!filter.journeyPlaceId || journeyPlace.place.id === filter.journeyPlaceId) &&
              (!filter.arrivalPlaceId ||
                journeyPlace.place.arrivalPoint.some(
                  (place) => place.id === filter.arrivalPlaceId
                ))
          )
          .map((journeyPlace) => ({
            ...journeyPlace,
            product: { id: product.id, name: product.name, country: product.country }
          }))
      ),
    [products, filter.journeyPlaceId, filter.arrivalPlaceId]
  )

  // the regions (journey places) and arrival places of the loaded products, for the filters
  const regionOptions = useMemo(() => {
    const places = products.flatMap((product) =>
      product.journeyPlaces.map((journeyPlace) => journeyPlace.place)
    )
    return filterDuplicates(places, (place) => place.id)
  }, [products])

  const arrivalPlaceOptions = useMemo(() => {
    const places = regionOptions
      .filter((place) => !filter.journeyPlaceId || place.id === filter.journeyPlaceId)
      .flatMap((place) => place.arrivalPoint)
    return filterDuplicates(places, (place) => place.id)
  }, [regionOptions, filter.journeyPlaceId])

  const selectedRows = journeyPlaces ?? []

  const columns: Column<ProductJourneyPlaceRow>[] = [
    {
      accessorKey: 'product.country.name',
      header: 'Pays',
      centerCell: true,
      cellClassName: 'px-0',
      cell: ({ row: { original: record } }) => <CountryFlag code={record.product.country.code} />
    },
    {
      accessorKey: 'product.name',
      header: 'Produit',
      cell: ({ row: { original: record } }) => (
        <div className="whitespace-pre-wrap break-words py-2">{record.product.name}</div>
      )
    },
    {
      accessorKey: 'place.name',
      header: 'Région de séjour',
      cell: ({ row: { original: record } }) => (
        <div className="whitespace-pre-wrap break-words py-2">{record.place.name}</div>
      )
    },
    {
      accessorKey: 'option.title',
      header: 'Thème / Option',
      cell: ({ row: { original: record } }) => (
        <div className="whitespace-pre-wrap break-words py-2">
          {getTranslatedName(record.option) || '-'}
        </div>
      )
    },
    {
      accessorKey: 'weeklyPrice',
      header: '',
      headerInfo: (
        <div className="flex flex-col">
          <span>Frais de séjour</span>
          <span className="font-normal text-grey">par semaine</span>
        </div>
      ),
      cell: ({ row: { original: record } }) => (
        <Badge variant="primary" text={`${record.weeklyPrice} €`} />
      )
    },
    {
      accessorKey: 'optionPrice',
      header: '',
      headerInfo: (
        <div className="flex flex-col">
          <span>{"Tarif de l'option"}</span>
          <span className="font-normal text-grey">par semaine</span>
        </div>
      ),
      cell: ({ row: { original: record } }) =>
        record.optionPrice ? <Badge variant="grey" text={`${record.optionPrice} €`} /> : '-'
    },
    {
      accessorKey: 'place.arrivalPoint',
      header: "Lieu(x) d'arrivée",
      cell: ({ row: { original: record } }) => (
        <div className="whitespace-pre-wrap break-words py-2">
          {record.place.arrivalPoint.length === 0
            ? '-'
            : record.place.arrivalPoint.map((place) => place.title).join(', ')}
        </div>
      )
    }
  ]

  return (
    <div>
      <div className="flex items-center justify-between">
        <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
          Filtrer :
        </Typography>
        <Button
          variant="destructive"
          size="small"
          icon={ResetTwoIcon}
          label="Réinitialiser"
          type="button"
          onClick={() => setFilter({})}
        />
      </div>
      <div className="h-3" />
      <div className="grid grid-cols-3 gap-3">
        <SelectInput<number>
          label={'Âge'}
          options={childAges.map((age) => ({ label: age.label, value: age.value }))}
          selectedOption={conditionalRender(!!filter.age, {
            label: childAges.find((age) => age.value === filter.age)?.label ?? '',
            value: filter.age!
          })}
          onChange={(age) => setFilter({ ...filter, age })}
        />
        <SelectInput<number>
          searchable
          label={'Pays'}
          options={countries?.data.map((country) => ({
            label: country.name,
            value: country.id
          }))}
          selectedOption={conditionalRender(!!filter.countryId, {
            label: countries?.data.find((country) => country.id === filter.countryId)?.name ?? '',
            value: filter.countryId!
          })}
          onChange={(countryId) => setFilter({ ...filter, countryId })}
        />
        <MultiSelectInput
          label={"# d'h de cours/sem"}
          variant={'primary'}
          options={OTO_INDIVIDUAL_NUMBER_OF_HOURS.map((hours) => ({
            label: `${hours}h`,
            value: hours
          }))}
          selectedOptions={filter.numberOfHours?.map((hours) => ({
            label: `${hours}h`,
            value: hours
          }))}
          onChange={(numberOfHours) => setFilter({ ...filter, numberOfHours })}
        />
        <SelectInput<number>
          label={'Région du séjour'}
          options={regionOptions.map((place) => ({ label: place.name, value: place.id }))}
          selectedOption={conditionalRender(!!filter.journeyPlaceId, {
            label: regionOptions.find((place) => place.id === filter.journeyPlaceId)?.name ?? '',
            value: filter.journeyPlaceId!
          })}
          onChange={(journeyPlaceId) =>
            setFilter({ ...filter, journeyPlaceId, arrivalPlaceId: undefined })
          }
        />
        <SelectInput<number>
          searchable
          label={"Lieu d'arrivée"}
          options={arrivalPlaceOptions.map((place) => ({ label: place.title, value: place.id }))}
          selectedOption={conditionalRender(!!filter.arrivalPlaceId, {
            label:
              arrivalPlaceOptions.find((place) => place.id === filter.arrivalPlaceId)?.title ?? '',
            value: filter.arrivalPlaceId!
          })}
          onChange={(arrivalPlaceId) => setFilter({ ...filter, arrivalPlaceId })}
        />
      </div>
      <div className="h-4" />
      <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
        {tableTitle}
      </Typography>
      <div className="h-2" />
      {isProductsPending ? (
        <Loader center />
      ) : isProductsError ? (
        <Typography variant="tag" color={tailwindColors.secondaryRed.DEFAULT}>
          Les lieux de séjour n’ont pas pu être chargés. Veuillez réessayer.
        </Typography>
      ) : (
        <DataTable<ProductJourneyPlaceRow>
          data={rows}
          columns={columns}
          rowCount={rows.length}
          mode="normal"
          paginated={false}
          manualSorting={false}
          selectable
          // every row is loaded client side, so "select all" is a plain toggle over the filtered rows
          // and never locks the individual checkboxes
          selectedData={{ isAllSelected: false, rows: selectedRows as ProductJourneyPlaceRow[] }}
          onSelectedDataChange={({ isAllSelected, rows: newRows }) => {
            if (!isAllSelected) return onSelectJourneyPlaces(newRows)

            const selectedIds = selectedRows.map((journeyPlace) => journeyPlace.id)
            const isEveryRowSelected = rows.every((row) => selectedIds.includes(row.id))
            if (isEveryRowSelected)
              // all the filtered rows were selected: the toggle deselects them
              return onSelectJourneyPlaces(
                (selectedRows as ProductJourneyPlaceRow[]).filter(
                  (journeyPlace) => !rows.some((row) => row.id === journeyPlace.id)
                )
              )

            onSelectJourneyPlaces([
              ...(selectedRows as ProductJourneyPlaceRow[]),
              ...rows.filter((row) => !selectedIds.includes(row.id))
            ])
          }}
          tableClassName="max-h-[480px] overflow-y-auto"
        />
      )}
      <div className="h-6" />
      <div className="flex w-full justify-end mb-4">
        <Button
          loading={isLoading}
          type="button"
          variant="primary"
          disabled={selectedRows.length === 0}
          onClick={onSubmit}
          label="Suivant"
        />
      </div>
    </div>
  )
}

export default ProductJourneyPlaceListSelection
